import {useState, useEffect} from 'react';

/**
 * 디바이스 타입
 */
export type DeviceType = 'mobile' | 'pc';

/**
 * User Agent로 모바일 디바이스 감지
 * @returns 모바일 여부
 */
export const isMobileByUserAgent = (): boolean => {
  const userAgent = navigator?.userAgent?.toLowerCase();

  // 모바일 디바이스 패턴들
  const mobilePatterns = [
    /android/i,
    /webos/i,
    /iphone/i,
    /ipad/i,
    /ipod/i,
    /blackberry/i,
    /windows phone/i,
    /mobile/i,
    /tablet/i,
  ];

  if (userAgent.includes('macintosh') && navigator.maxTouchPoints > 0) {
    return true;
  }

  return mobilePatterns.some((pattern) => pattern.test(userAgent));
};

/**
 * 화면 크기로 모바일 디바이스 감지
 * @param breakpoint 브레이크포인트 (기본값: 768px)
 * @returns 모바일 여부
 */
export const isMobileByScreenSize = (breakpoint: number = 768): boolean => {
  return window.innerWidth <= breakpoint;
};

/**
 * 디바이스 감지 훅
 * @param detectionMethod 감지 방법 ('screen' | 'userAgent' | 'hybrid')
 * @param breakpoint 화면 크기 기준 브레이크포인트 (detectionMethod가 'screen' 또는 'hybrid'일 때 사용)
 * @returns 디바이스 타입
 */
export const useDeviceDetection = (
  detectionMethod: 'screen' | 'userAgent' | 'hybrid' = 'screen',
  breakpoint: number = 768,
): DeviceType | undefined => {
  const [deviceType, setDeviceType] = useState<DeviceType>();

  useEffect(() => {
    const checkDevice = () => {
      let isMobile = false;

      switch (detectionMethod) {
        case 'screen':
          isMobile = isMobileByScreenSize(breakpoint);
          break;
        case 'userAgent':
          isMobile = isMobileByUserAgent();
          break;
        case 'hybrid':
          // User Agent와 화면 크기 모두 확인 (둘 중 하나라도 모바일이면 모바일로 판단)
          isMobile = isMobileByUserAgent() || isMobileByScreenSize(breakpoint);
          break;
        default:
          isMobile = isMobileByScreenSize(breakpoint);
      }

      setDeviceType(isMobile ? 'mobile' : 'pc');
    };

    // 초기 체크
    checkDevice();

    // 리사이즈 이벤트 리스너 (화면 크기 기반 감지일 때만)
    if (detectionMethod === 'screen' || detectionMethod === 'hybrid') {
      window.addEventListener('resize', checkDevice);

      return () => {
        window.removeEventListener('resize', checkDevice);
      };
    }
  }, [detectionMethod, breakpoint]);

  return deviceType;
};

/**
 * 사용 예시:
 *
 * // 1. 화면 크기로만 감지 (기본값)
 * const deviceType = useDeviceDetection();
 *
 * // 2. User Agent로만 감지
 * const deviceType = useDeviceDetection('userAgent');
 *
 * // 3. 하이브리드 방식 (User Agent + 화면 크기)
 * const deviceType = useDeviceDetection('hybrid');
 *
 * // 4. 커스텀 브레이크포인트
 * const deviceType = useDeviceDetection('screen', 1024);
 *
 * // 5. 하이브리드 + 커스텀 브레이크포인트
 * const deviceType = useDeviceDetection('hybrid', 1024);
 */
